import Box from '@mui/material/Box';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import ListAltIcon from '@mui/icons-material/ListAlt';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import SettingsIcon from '@mui/icons-material/Settings';
import PrintIcon from '@mui/icons-material/Print';
import SendIcon from '@mui/icons-material/Send';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';

import { environment } from '../../environment';
import { MemoBeforeSubmitForm } from '../../forms/MemoBeforeSubmit';

import { Tabs, TabsStrategy, TabsVariant } from '../Tabs';

import { Actions } from './Actions';
import { EditionDrawer } from './EditionDrawer';
import { EditorMode } from './enums/editor-mode.enum';
import { GenerationTabPanel } from './tabs/Generation';
import { EditorTabPanel } from './tabs/Editor';
import { NavigationBlocker } from './NavigationBlocker';
import { SummaryTabPanel } from './tabs/Summary';
import { PrinterTabPanel } from './tabs/Printer';
import { PreviewTabPanel } from './tabs/Preview';
import { ShareTabPanel } from './tabs/Share';
import { VersionsTabPanel } from './tabs/Versions';
import { AdminTabPanel } from './tabs/Admin';
import { useDocumentEditorContext } from './contexts/DocumentEditorContext/hooks/useDocumentEditorContext.hook';

export const DocumentEditor = () => {
  const {
    showMemoBeforeSave,
    clearSaveMemos,
    saveMemos,
    saveSubmitHandler,
    setSaveMemos,
    props: {
      componentSettings: { InfoTabPanel },
      formSettings: { isLoading },
      routerSettings: { editorModePaths, isInPrintMode },
    },
    currentEditorMode,
  } = useDocumentEditorContext();

  return (
    <>
      <NavigationBlocker />
      {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
      {!!showMemoBeforeSave && (
        <MemoBeforeSubmitForm
          clearMemos={clearSaveMemos}
          isLoading={isLoading}
          memos={saveMemos}
          onConfirm={saveSubmitHandler}
          setMemos={setSaveMemos}
          submitLabel="Enregistrer"
        />
      )}
      <Box
        sx={
          isInPrintMode
            ? { display: 'block' }
            : { display: 'flex', flexGrow: 1 }
        }
      >
        <Tabs
          activeTab={currentEditorMode}
          id="document-editor-tabs"
          isInPrintMode={isInPrintMode}
          strategy={TabsStrategy.Controlled}
          tabs={[
            {
              key: EditorMode.Info,
              icon: <InfoIcon />,
              label: 'Informations',
              panel: <InfoTabPanel />,
              to: editorModePaths.Info,
              value: EditorMode.Info,
              disabled: !editorModePaths.Info,
            },
            {
              key: EditorMode.Editor,
              icon: <EditIcon />,
              label: 'Éditeur',
              panel: <EditorTabPanel />,
              to: editorModePaths.Editor,
              value: EditorMode.Editor,
              disabled: !editorModePaths.Editor,
            },
            {
              key: EditorMode.Summary,
              icon: <ListAltIcon />,
              label: 'Table des Matières',
              panel: <SummaryTabPanel />,
              to: editorModePaths.Summary,
              value: EditorMode.Summary,
              disabled: !editorModePaths.Summary,
            },
            {
              key: EditorMode.Printer,
              icon: <PrecisionManufacturingIcon />,
              label: 'Imprimante',
              panel: <PrinterTabPanel />,
              to: editorModePaths.Printer,
              value: EditorMode.Printer,
              hiddenTab: !environment.documentEditorShowPrinterTab,
              disabled: !editorModePaths.Printer,
            },
            {
              key: EditorMode.Preview,
              icon: <VisibilityIcon />,
              label: 'Aperçu',
              panel: <PreviewTabPanel />,
              to: editorModePaths.Preview,
              value: EditorMode.Preview,
              disabled: !editorModePaths.Preview,
            },
            {
              key: EditorMode.Generation,
              icon: <PrintIcon />,
              label: 'Génération',
              panel: <GenerationTabPanel />,
              to: editorModePaths.Generation,
              value: EditorMode.Generation,
              disabled: !editorModePaths.Generation,
            },
            {
              key: EditorMode.Share,
              icon: <SendIcon />,
              label: 'Partage',
              panel: <ShareTabPanel />,
              to: editorModePaths.Share,
              value: EditorMode.Share,
              disabled: !editorModePaths.Share,
            },
            {
              key: EditorMode.Versions,
              icon: <FormatListNumberedIcon />,
              label: 'Versions',
              panel: <VersionsTabPanel />,
              to: editorModePaths.Versions,
              value: EditorMode.Versions,
              disabled: !editorModePaths.Versions,
            },
            {
              key: EditorMode.Admin,
              icon: <SettingsIcon />,
              label: 'Administration',
              panel: <AdminTabPanel />,
              to: editorModePaths.Admin,
              value: EditorMode.Admin,
              disabled: !editorModePaths.Admin,
            },
          ]}
          title="Modes de l'éditeur de document"
          variant={TabsVariant.Vertical}
        />
        {!isInPrintMode && (
          <>
            <EditionDrawer />
            <Actions />
          </>
        )}
      </Box>
    </>
  );
};
